import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Text,
    useStyleConfig,
    useMediaQuery,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Box as ChakraBox} from '@chakra-ui/react'
import {getDamImagePicture} from '../../../custom-hooks/use-dam-image'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {ChevronRight} from '../../icons'
import {Link as RouteLink} from 'react-router-dom'
import {remCalc} from '../../../theme/_utils'

const MenuVerticalCard = ({card, categoriesData, locale, showChevron = true}) => {
    const styles = useStyleConfig('PlpTopCards', {})
    const [isHovered, setIsHovered] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [isMobileOrTablet] = useMediaQuery('(max-width: 1199px)')

    const imagePicture = getDamImagePicture(
        card?.image,
        '',
        false,
        undefined,
        {objectFit: 'cover', objectPosition: '50%', display: 'block'},
        null,
        true,
    )

    const {url, isAbsolute, label} =
        getGlobalLink({
            link: card?.redirect,
            categoriesData,
            locale: locale?.alias,
        }) || {}

    const resolved_title = label ? label : card?.title && card?.title !== '' ? card.title : null
    const resolved_subtitle = card?.subtitle && card?.subtitle !== '' ? card.subtitle : null

    const shouldShowButton = isHovered || isFocused || isMobileOrTablet

    return (
        <Box
            as={url ? (isAbsolute ? 'a' : RouteLink) : undefined}
            href={url && isAbsolute ? url : undefined}
            to={url && !isAbsolute ? url : undefined}
            target={url && isAbsolute ? '_blank' : undefined}
            rel={url && isAbsolute ? 'noopener noreferrer' : undefined}
            aria-label={url ? resolved_title || resolved_subtitle || 'Open link' : undefined}
            width="100%"
            height="100%"
            minHeight="100%"
            display="block"
            position="relative"
            overflow="hidden"
            borderRadius="large-1"
            backgroundColor="#E1E1E3"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            transition="background-size 0.4s ease"
            textDecoration="none"
        >
            {imagePicture && (
                <Box position="absolute" inset="0" pointerEvents="none">
                    <Box
                        position="absolute"
                        inset="0"
                        transform={isHovered ? 'scale(1.05)' : 'scale(1)'}
                        transition="transform 0.6s ease"
                    >
                        {imagePicture}
                    </Box>
                    <Box
                        position="absolute"
                        inset="0"
                        sx={{
                            filter: `blur(${remCalc(10)})`,
                            transform: 'scale(1.01)',
                            maskImage: 'linear-gradient(transparent 75%, black 82%)',
                            WebkitMaskImage: 'linear-gradient(transparent 75%, black 82%)',
                        }}
                    >
                        {imagePicture}
                    </Box>
                </Box>
            )}

            <Box
                position="absolute"
                inset="0"
                background="linear-gradient(180deg, transparent 45%, rgba(0, 0, 0, 0.45) 100%)"
                pointerEvents="none"
            />

            <Flex
                position="absolute"
                left="0"
                right="0"
                bottom="0"
                alignItems="flex-end"
                justifyContent="space-between"
                gap="xsmall.base"
                padding="xsmall-1.base"
            >
                <Flex flexDirection="column" gap={remCalc(4)} minW="0">
                    {resolved_title && (
                        <ChakraBox>
                            <Text
                                textStyle="body-medium"
                                fontWeight="medium"
                                color="foreground.neutral.inverse"
                                noOfLines={1}
                            >
                                {resolved_title}
                            </Text>
                        </ChakraBox>
                    )}

                    {resolved_subtitle && (
                        <ChakraBox>
                            <Text
                                textStyle="body-medium"
                                color="foreground.neutral.inverse"
                                noOfLines={2}
                            >
                                {resolved_subtitle}
                            </Text>
                        </ChakraBox>
                    )}
                </Flex>

                {showChevron && url && (
                    <Flex
                        {...styles?.buttonContainer}
                        opacity={shouldShowButton ? 0.5 : 0}
                        transition="opacity 0.2s ease"
                        pointerEvents={shouldShowButton ? 'auto' : 'none'}
                        flexShrink={0}
                    >
                        <ChevronRight {...styles?.chevronIcon} />
                    </Flex>
                )}
            </Flex>
        </Box>
    )
}

MenuVerticalCard.propTypes = {
    card: PropTypes.object,
    categoriesData: PropTypes.object,
    locale: PropTypes.object,
    showChevron: PropTypes.bool,
}

export default MenuVerticalCard
